import { createContext, useEffect, useRef, useState, type ReactNode } from "react";
import { initSupabase, getSupabase } from "@/lib/supabase";
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SESSION_EXPIRED_MESSAGE_KEY,
  DEFAULT_SESSION_EXPIRED_MESSAGE,
  getSafeInternalReturnTo,
  type AuthSessionExpiredDetail,
} from "@/lib/authSessionExpired";
import type { User, Session, AuthError, SupabaseClient } from "@supabase/supabase-js";
import { usePostHog } from "@posthog/react";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const sessionExpiryHandlingRef = useRef(false);

  // PostHog for user identification (may be null if PostHog not configured)
  const posthog = usePostHog();

  useEffect(() => {
    let subscription: any;

    // Initialize Supabase and setup auth
    initSupabase()
      .then((client) => {
        setSupabase(client);

        // Get initial session
        return client.auth.getSession();
      })
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Listen for auth changes
        const client = getSupabase();
        const {
          data: { subscription: sub },
        } = client.auth.onAuthStateChange((_event, session) => {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        });

        subscription = sub;
      })
      .catch((err) => {
        console.error("Failed to initialize auth:", err);
        posthog?.captureException(err instanceof Error ? err : new Error(String(err)), {
          $exception_fingerprint: "AuthContext:initializeAuth",
          $exception_level: "fatal",
          source: "AuthContext",
          operation: "initializeAuth",
        });
        setError(err);
        setLoading(false);
      });

    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    const handleSessionExpired = async (event: Event) => {
      if (sessionExpiryHandlingRef.current) return;
      sessionExpiryHandlingRef.current = true;

      const detail = (event as CustomEvent<AuthSessionExpiredDetail>).detail || {};
      const returnTo = getSafeInternalReturnTo(
        detail.returnTo || `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );

      try {
        window.sessionStorage.setItem(
          AUTH_SESSION_EXPIRED_MESSAGE_KEY,
          detail.message || DEFAULT_SESSION_EXPIRED_MESSAGE,
        );
      } catch {
        // Storage can fail in restricted browser modes. The query param still explains the redirect.
      }

      if (window.location.pathname !== "/signin") {
        const params = new URLSearchParams({
          reason: "session_expired",
          returnTo,
        });
        window.location.assign(`/signin?${params.toString()}`);

        try {
          const client = supabase || getSupabase();
          void client.auth.signOut({ scope: "local" });
        } catch (err) {
          console.warn("[Auth] Local sign-out after session expiry failed:", err);
        }
        return;
      }

      setSession(null);
      setUser(null);
      setError(null);

      try {
        const client = supabase || getSupabase();
        await client.auth.signOut({ scope: "local" });
      } catch (err) {
        console.warn("[Auth] Local sign-out after session expiry failed:", err);
      }
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [supabase]);

  useEffect(() => {
    if (session) {
      sessionExpiryHandlingRef.current = false;
    }
  }, [session?.access_token]);

  // PostHog user identification - identify on login, reset on logout
  useEffect(() => {
    if (!posthog) return;

    if (user) {
      // Identify user with PostHog (sends $identify event)
      posthog.identify(user.id, {
        email: user.email,
        created_at: user.created_at,
      });
    } else {
      // Reset PostHog when user logs out (clears distinct_id)
      posthog.reset();
    }
  }, [posthog, user?.id, user?.email, user?.created_at]);

  const signIn = async (email: string, password: string) => {
    if (!supabase) throw new Error("Supabase not initialized");
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error) {
      setError(error as AuthError);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    if (!supabase) throw new Error("Supabase not initialized");
    try {
      setError(null);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      setError(error as AuthError);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    if (!supabase) throw new Error("Supabase not initialized");
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (error) {
      setError(error as AuthError);
      throw error;
    }
  };

  const signOut = async () => {
    if (!supabase) throw new Error("Supabase not initialized");
    try {
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      setError(error as AuthError);
      throw error;
    }
  };

  const clearError = () => setError(null);

  const value = {
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
